import axios from "axios";
import { useEffect, useContext, useState } from "react";
import { AuthContext } from "../context/AuthContext";
import { NavigationContext } from "../context/NavigationContext";
import {
    Table,
    Spin,
    Typography,
    message,
    Button,
    Select,
    Space,
    Input,
    Upload,
    DatePicker,
    Row,
    Col,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

export default function ListTickets() {
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(false);

    const [updatingId, setUpdatingId] = useState(null);
    const [statusChange, setStatusChange] = useState({});
    const [formValues, setFormValues] = useState({});

    const { authData, backUrl } = useContext(AuthContext);
    const { navigationParams, clearNavigationParams } = useContext(NavigationContext);

    // -------------------------
    // FILTER STATES
    // -------------------------
    const [filters, setFilters] = useState({
        ticketId: "",
        status: "",
        priority: "",
        department: "",
        dateRange: null,
    });

    // ------------------------------------------------
    // FETCH TICKETS
    // ------------------------------------------------
    const fetchData = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${backUrl}/api/hr/tickets/inbox/`, {
                headers: {
                    Authorization: `Bearer ${authData?.access}`,
                },
            });

            const result = res.data.results || res.data;
            setData(result);
            setFilteredData(result); // initial load
        } catch (error) {
            console.error("Error fetching tickets:", error);
            message.error("Failed to load tickets.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (authData?.access) fetchData();
    }, [authData]);
    
    useEffect(() => {
        if (navigationParams?.status) {
            setFilters(prev => ({ ...prev, status: navigationParams.status.toUpperCase() }));
            clearNavigationParams();
        }
    }, [navigationParams, clearNavigationParams]);

    // ------------------------------------------------
    // FILTER LOGIC
    // ------------------------------------------------
    useEffect(() => {
        let temp = [...data];

        // Ticket ID
        if (filters.ticketId) {
            temp = temp.filter((item) =>
                String(item.ticket_number)
                    .toLowerCase()
                    .includes(filters.ticketId.toLowerCase())
            );
        }

        // Status
        if (filters.status) {
            temp = temp.filter((item) => item.status === filters.status);
        }

        // Priority
        if (filters.priority) {
            temp = temp.filter((item) => item.priority === filters.priority);
        }

        // Department
        if (filters.department) {
            temp = temp.filter(
                (item) =>
                    String(item.created_by?.department_name).toLowerCase() ===
                    filters.department.toLowerCase()
            );
        }

        // Date Range Filter
        if (filters.dateRange && filters.dateRange.length === 2) {
            const [start, end] = filters.dateRange;
            temp = temp.filter((item) => {
                const created = new Date(item.created_at);
                return created >= start.toDate() && created <= end.toDate();
            });
        }

        setFilteredData(temp);
    }, [filters, data]);

    // ------------------------------------------------
    // HANDLE ACKNOWLEDGE
    // ------------------------------------------------
    const handleStatusSubmit = async (ticketId) => {
        const { status, remark, attachment } = formValues[ticketId] || {};

        if (!status) return message.warning("Please select a status.");
        if (!remark) return message.warning("Please enter a remark.");

        const formData = new FormData();
        formData.append("status", status);
        formData.append("remark", remark);
        if (attachment) formData.append("attachment", attachment);

        try {
            setUpdatingId(ticketId);
            await axios.post(
                `${backUrl}/api/hr/tickets/${ticketId}/acknowledge/`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            message.success("Ticket acknowledged successfully!");
            setStatusChange((prev) => ({ ...prev, [ticketId]: false }));
            setFormValues((prev) => ({ ...prev, [ticketId]: {} }));
            fetchData();
        } catch (error) {
            console.error("Error acknowledging ticket:", error);
            message.error("Failed to acknowledge ticket.");
        } finally {
            setUpdatingId(null);
        }
    };

    const handleFormChange = (ticketId, key, value) => {
        setFormValues((prev) => ({
            ...prev,
            [ticketId]: {
                ...prev[ticketId],
                [key]: value,
            },
        }));
    };

    // ------------------------------------------------
    // TABLE COLUMNS
    // ------------------------------------------------
    const columns = [
        {
            title: "Ticket ID",
            dataIndex: "ticket_number",
        },
        {
            title: "Subject",
            dataIndex: "subject",
        },
        {
            title: "Attachment",
            dataIndex: "attachment",
            render: (text) =>
                text ? (
                    <Button
                        type="link"
                        onClick={() => window.open(text, "_blank")}
                    >
                        View
                    </Button>
                ) : (
                    "-"
                ),
        },
        {
            title: "Description",
            dataIndex: "description",
        },
        {
            title: "Created By",
            dataIndex: ["created_by", "first_name"],
            render: (text) => text || "—",
        },
        {
            title: "Department",
            dataIndex: ["created_by", "department_name"],
        },
        {
            title: "Priority",
            dataIndex: "priority",
        },
        {
            title: "Status",
            dataIndex: "status",
            render: (text) => (
                <span
                    style={{
                        color:
                            text === "Open"
                                ? "green"
                                : text === "Closed"
                                    ? "red"
                                    : "orange",
                    }}
                >
                    {text}
                </span>
            ),
        },
        {
            title: "Created At",
            dataIndex: "created_at",
            render: (text) =>
                new Date(text).toLocaleDateString("en-GB") || "—",
        },
        {
            title: "Action",
            render: (_, record) => (
                <Space direction="vertical">
                    {!statusChange[record.id] ? (
                        <Button
                            type="primary"
                            size="small"
                            onClick={() =>
                                setStatusChange((prev) => ({
                                    ...prev,
                                    [record.id]: true,
                                }))
                            }
                            style={{ backgroundColor: "#0e497b" }}
                        >
                            Acknowledge
                        </Button>
                    ) : (
                        <>
                            <Select
                                placeholder="Select Status"
                                style={{ width: "150px" }}
                                onChange={(value) =>
                                    handleFormChange(record.id, "status", value)
                                }
                            >
                                {!authData?.employee?.is_superuser && (
                                    <Option value="IN_PROGRESS">
                                        IN_PROGRESS
                                    </Option>
                                )}
                                <Option value="RESOLVED">RESOLVED</Option>
                            </Select>


                            <>
                                <TextArea
                                    rows={2}
                                    placeholder="Enter remark"
                                    onChange={(e) =>
                                        handleFormChange(
                                            record.id,
                                            "remark",
                                            e.target.value
                                        )
                                    }
                                />

                                {!authData?.employee?.is_superuser && (
                                    <Upload
                                        beforeUpload={(file) => {
                                            handleFormChange(
                                                record.id,
                                                "attachment",
                                                file
                                            );
                                            return false;
                                        }}
                                        maxCount={1}
                                    >
                                        <Button icon={<UploadOutlined />}>
                                            Attach File
                                        </Button>
                                    </Upload>

                                )}
                            </>

                            <Space>
                                <Button
                                    type="primary"
                                    size="small"
                                    loading={updatingId === record.id}
                                    onClick={() =>
                                        handleStatusSubmit(record.id)
                                    }
                                >
                                    Submit
                                </Button>

                                <Button
                                    size="small"
                                    onClick={() =>
                                        setStatusChange((prev) => ({
                                            ...prev,
                                            [record.id]: false,
                                        }))
                                    }
                                >
                                    Cancel
                                </Button>
                            </Space>
                        </>
                    )}
                </Space>
            ),
        },
    ];

    // ------------------------------------------------
    // RENDER UI
    // ------------------------------------------------
    return (
        <>
            <Title level={5}>Ticket List</Title>

            {/* ---------------- FILTER SECTION ---------------- */}
            <div
                style={{
                    padding: "15px",
                    marginBottom: "15px",
                    background: "#fafafa",
                    borderRadius: "8px",
                }}
            >
                <Row gutter={[12, 12]}>
                    <Col xs={24} sm={12} md={6}>
                        <Input
                            placeholder="Search Ticket ID"
                            value={filters.ticketId}
                            onChange={(e) =>
                                setFilters({
                                    ...filters,
                                    ticketId: e.target.value,
                                })
                            }
                        />
                    </Col>

                    <Col xs={24} sm={12} md={6}>
                        <Select
                            placeholder="Select Status"
                            style={{ width: "100%" }}
                            allowClear
                            onChange={(val) =>
                                setFilters({ ...filters, status: val })
                            }
                        >
                            <Option value="NEW">NEW</Option>
                            <Option value="IN_PROGRESS">IN_PROGRESS</Option>
                            <Option value="RESOLVED">RESOLVED</Option>
                        </Select>
                    </Col>

                    <Col xs={24} sm={12} md={6}>
                        <Select
                            placeholder="Select Priority"
                            style={{ width: "100%" }}
                            allowClear
                            onChange={(val) =>
                                setFilters({ ...filters, priority: val })
                            }
                        >
                            <Option value="Low">Low</Option>
                            <Option value="Medium">Medium</Option>
                            <Option value="High">High</Option>
                        </Select>
                    </Col>

                    <Col xs={24} sm={12} md={6}>
                        <Input
                            placeholder="Department"
                            allowClear
                            onChange={(e) =>
                                setFilters({
                                    ...filters,
                                    department: e.target.value,
                                })
                            }
                        />
                    </Col>

                    <Col xs={24} sm={12} md={6}>
                        <RangePicker
                            style={{ width: "100%" }}
                            onChange={(dates) =>
                                setFilters({
                                    ...filters,
                                    dateRange: dates,
                                })
                            }
                        />
                    </Col>
                </Row>
            </div>

            {loading ? (
                <div style={{ textAlign: "center", marginTop: 40 }}>
                    <Spin size="large" />
                </div>
            ) : (
                <Table
                    dataSource={filteredData}
                    columns={columns}
                    rowKey="id"
                    pagination={{ pageSize: 10 }}
                    bordered
                    components={{
                        header: {
                            cell: (props) => (
                                <th {...props} style={{ backgroundColor: "#0e497b", color: 'white', marginTop: '20px' }}>
                                    {props.children}
                                </th>
                            ),
                        },
                    }}
                />
            )}
        </>
    );
}
